import React, { useState, useEffect, useCallback } from 'react';
import { Box, Typography } from '@mui/material';
import ButtonComponent from '../customButton';
import ModalWrapper from '../ModalWrapper';
import { CommonFooter } from '../FormComponent';
import { listSlackLinks, unlinkSlackAccount } from '../../utils/api/slack_link_apis';
import { STATUS } from '../../utils/api/sharedfunction';
import { useSnackbar } from '../../utils/useSnackbar';
import moment from 'moment';

const LinkedSlackAccounts = ({ refreshSignal }) => {
  const { showSnackbar } = useSnackbar();
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);
  const [unlinkingId, setUnlinkingId] = useState(null);
  const [unlinkConfirmOpen, setUnlinkConfirmOpen] = useState(false);
  const [linkToUnlink, setLinkToUnlink] = useState(null);

  const refreshLinks = useCallback(() => {
    setRefreshKey((k) => k + 1);
  }, []);

  useEffect(() => {
    let isMounted = true;
    const fetchLinks = async () => {
      setLoading(true);
      try {
        const response = await listSlackLinks();
        if (!isMounted) return;
        if (response.status === STATUS.SUCCESS) {
          setLinks(Array.isArray(response.data) ? response.data : []);
        } else {
          showSnackbar(response.error || 'Failed to load linked Slack accounts', 'error');
        }
      } catch (err) {
        if (isMounted) showSnackbar(err.message || 'Failed to load linked Slack accounts', 'error');
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    fetchLinks();
    return () => { isMounted = false; };
    // refreshSignal lets a parent (e.g. after creating a token via the
    // slackLinkToken prompt flow) force a refetch, since linking happens as
    // a side effect of token creation elsewhere and this component has no
    // other way to know a new link may now exist.
  }, [refreshKey, refreshSignal]);

  const openUnlinkConfirm = (link) => {
    setLinkToUnlink(link);
    setUnlinkConfirmOpen(true);
  };

  const closeUnlinkConfirm = () => {
    if (!unlinkingId) {
      setUnlinkConfirmOpen(false);
      setLinkToUnlink(null);
    }
  };

  const handleUnlinkConfirm = async () => {
    if (!linkToUnlink?.id) return;
    setUnlinkingId(linkToUnlink.id);
    try {
      const response = await unlinkSlackAccount(linkToUnlink.id);
      if (response.status === STATUS.SUCCESS) {
        showSnackbar(response.message || 'Slack account unlinked', 'success');
        refreshLinks();
        setUnlinkConfirmOpen(false);
        setLinkToUnlink(null);
      } else {
        showSnackbar(response.error || 'Failed to unlink Slack account', 'error');
      }
    } catch (err) {
      showSnackbar(err.message || 'Failed to unlink Slack account', 'error');
    } finally {
      setUnlinkingId(null);
    }
  };

  if (!loading && links.length === 0) {
    return null;
  }

  return (
    <Box sx={{ width: '100%', pb: 3 }}>
      <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 0.5 }}>
        Linked Slack Accounts
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Slack workspaces where your QA-Path identity is linked, so @-mentioning the bot acts as you.
      </Typography>

      {loading ? (
        <Typography variant="body2" color="text.secondary">Loading...</Typography>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {links.map((link) => (
            <Box
              key={link.id}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                p: 1.5,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
              }}
            >
              <Box>
                <Typography variant="body2" fontWeight={500}>
                  Slack workspace {link.slackTeamId}
                </Typography>
                <Typography variant="caption" color="text.secondary" component="div">
                  Linked {link.createdAt ? moment(link.createdAt).format('YYYY-MM-DD') : ''} via token{' '}
                  {link.tokenName || 'Unknown'} ({link.maskedKey || 'unknown'})
                </Typography>
                {link.tokenStatus && link.tokenStatus !== 'ACTIVE' && (
                  <Typography variant="caption" color="error" component="div">
                    This token is {link.tokenStatus.toLowerCase()} - Slack replies will fail until you re-link.
                  </Typography>
                )}
              </Box>
              <ButtonComponent
                variant="secondary"
                ctaText="Unlink"
                size="small"
                onClick={() => openUnlinkConfirm(link)}
                disabled={unlinkingId === link.id}
                className="text-red-600 hover:bg-red-50"
              />
            </Box>
          ))}
        </Box>
      )}

      <ModalWrapper
        isOpen={unlinkConfirmOpen}
        onClose={closeUnlinkConfirm}
        title="Unlink Slack account"
        size="small"
        closeOnBackdrop={false}
        footer={
          <CommonFooter
            buttons={[
              {
                label: 'Cancel',
                variant: 'outline',
                className: 'primary',
                onClick: closeUnlinkConfirm,
                disabled: !!unlinkingId,
              },
              {
                label: unlinkingId ? 'Unlinking...' : 'Unlink',
                variant: 'primary',
                className: 'white bg-blue600',
                onClick: handleUnlinkConfirm,
                disabled: !!unlinkingId,
                loading: !!unlinkingId,
              },
            ]}
            align="right"
          />
        }
      >
        <Box sx={{ px: 2, pb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            {linkToUnlink
              ? `Unlink Slack workspace ${linkToUnlink.slackTeamId}? You'll need to re-link (mention the bot again) to use it there.`
              : 'Unlink this Slack account?'}
          </Typography>
        </Box>
      </ModalWrapper>
    </Box>
  );
};

export default LinkedSlackAccounts;
